import React, { useState, ReactNode, useCallback, useMemo, useEffect } from 'react'
import { Platform, useWindowDimensions } from 'react-native'

import { CreationStackContext, type CreationStackType, type CreationStackStep } from './CreationStackContext'
import { useLanguage } from './LanguageContext'

// All screen components are lazy-loaded to avoid circular dependencies:
// CreationStackProvider → screens → @context / @hooks / @components → CreationStackProvider
function buildCreationStacks(
  t: (key: string) => string,
  screens: { EditNote: React.ComponentType<any>; Conversation: React.ComponentType<any>; AddEvent: React.ComponentType<any>; CreateScheduledMessage: React.ComponentType<any>; ComposeMessage: React.ComponentType<any> },
  AddKeep: React.ComponentType<any>
): Record<CreationStackType, CreationStackStep[]> {
  return {
    note: [
      { component: screens.EditNote, title: t('creationStack.addNote') },
    ],
    message: [
      { component: screens.Conversation, title: t('creationStack.conversation') },
    ],
    reminder: [
      { component: screens.AddEvent, title: t('creationStack.createEvent') },
    ],
    keepContact: [
      { component: AddKeep, title: t('creationStack.addReminder') },
    ],
    scheduledMessage: [
      { component: screens.CreateScheduledMessage, title: t('creationStack.scheduleMessage') },
      { component: screens.ComposeMessage, title: t('creationStack.composeMessage') },
    ],
  }
}

interface CreationStackProviderProps {
  children: ReactNode
}

export const CreationStackProvider: React.FC<CreationStackProviderProps> = ({ children }) => {
  const { t } = useLanguage()
  const [isVisible, setIsVisible] = useState(false)
  const [currentStack, setCurrentStack] = useState<{ type: CreationStackType; steps: CreationStackStep[]; currentStepIndex: number; data: Record<string, any> } | null>(null)
  const [lazyModules, setLazyModules] = useState<{
    screens: { EditNote: React.ComponentType<any>; Conversation: React.ComponentType<any>; AddEvent: React.ComponentType<any>; CreateScheduledMessage: React.ComponentType<any>; ComposeMessage: React.ComponentType<any> }
    AddKeep: React.ComponentType<any>
  } | null>(null)

  useEffect(() => {
    Promise.all([
      import('@screens'),
      import('../screens/menu/keepContact/AddKeep'),
    ]).then(([screensModule, addKeepModule]) => {
      setLazyModules({ screens: screensModule, AddKeep: addKeepModule.default })
    })
  }, [])

  const { width } = useWindowDimensions()
  const isNonMobile = width >= 640
  const isWeb = Platform.OS === 'web'
  const isCreationStackEnabled = isWeb && isNonMobile

  const creationStacks = useMemo(() => {
    if (!lazyModules) return null
    return buildCreationStacks(t, lazyModules.screens, lazyModules.AddKeep)
  }, [t, lazyModules])

  const showCreationStack = useCallback((type: CreationStackType, initialData: Record<string, any> = {}, stepIndex: number = 0) => {
    if (!isCreationStackEnabled) {
      console.warn('Creation stack is not enabled for this platform/screen size')
      return
    }
    if (!creationStacks) return
    const stack = creationStacks[type]
    if (!stack) {
      console.error(`Unknown creation stack type: ${type}`)
      return
    }
    setCurrentStack({
      type,
      steps: stack,
      currentStepIndex: stepIndex,
      data: initialData,
    })
    setIsVisible(true)
  }, [isCreationStackEnabled, creationStacks])

  const hideCreationStack = useCallback(() => {
    setIsVisible(false)
    setCurrentStack(null)
  }, [])

  const nextStep = useCallback(() => {
    if (!currentStack) return
    const nextIndex = currentStack.currentStepIndex + 1
    if (nextIndex < currentStack.steps.length) {
      setCurrentStack(prev => prev ? { ...prev, currentStepIndex: nextIndex } : null)
    }
  }, [currentStack])

  const previousStep = useCallback(() => {
    if (!currentStack) return
    const prevIndex = currentStack.currentStepIndex - 1
    if (prevIndex >= 0) {
      setCurrentStack(prev => prev ? { ...prev, currentStepIndex: prevIndex } : null)
    } else hideCreationStack()
  }, [currentStack, hideCreationStack])

  const goToStep = useCallback((stepIndex: number) => {
    if (!currentStack) return
    if (stepIndex >= 0 && stepIndex < currentStack.steps.length) {
      setCurrentStack(prev => prev ? { ...prev, currentStepIndex: stepIndex } : null)
    }
  }, [currentStack])

  const updateStackData = useCallback((data: Record<string, any>) => {
    if (!currentStack) return
    setCurrentStack(prev => prev ? { ...prev, data: { ...prev.data, ...data } } : null)
  }, [currentStack])

  const resetStack = useCallback(() => {
    setCurrentStack(null)
    setIsVisible(false)
  }, [])

  const completeStep = useCallback((stepData?: Record<string, any>) => {
    if (!currentStack) return
    if (stepData) updateStackData(stepData)
    if (currentStack.currentStepIndex === currentStack.steps.length - 1) {
      resetStack()
    } else {
      nextStep()
    }
  }, [currentStack, updateStackData, resetStack, nextStep])

  const canGoBack = currentStack ? currentStack.currentStepIndex > 0 : false
  const canGoNext = currentStack ? currentStack.currentStepIndex < currentStack.steps.length - 1 : false
  const isLastStep = currentStack ? currentStack.currentStepIndex === currentStack.steps.length - 1 : false
  const isFirstStep = currentStack ? currentStack.currentStepIndex === 0 : false

  const contextValue = {
    isVisible,
    currentStack,
    showCreationStack,
    hideCreationStack,
    nextStep,
    previousStep,
    goToStep,
    updateStackData,
    resetStack,
    completeStep,
    canGoBack,
    canGoNext,
    isLastStep,
    isFirstStep,
    isCreationStackEnabled,
  }

  return (
    <CreationStackContext.Provider value={contextValue}>
      {children}
    </CreationStackContext.Provider>
  )
}
